import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { DollarSign, Users, CheckCircle, XCircle, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminSpreadPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getSpreadReport();
      setReport(data);
    } catch { toast.error('Failed to load spread report'); }
    setLoading(false);
  };

  useEffect(() => { loadReport(); }, []);

  const handleMarkPaid = async (userId, name) => {
    setActionLoading(userId + '_pay');
    try {
      const { data } = await adminAPI.markSpreadPaid(userId);
      toast.success(`Spread marked as paid for ${name} (${data.trades_updated} trades)`);
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(null);
  };

  const handleReset = async (userId, name) => {
    setActionLoading(userId + '_reset');
    try {
      const { data } = await adminAPI.resetSpread(userId);
      toast.success(`Spread reset for ${name} (${data.trades_updated} trades)`);
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(null);
  };

  const s = report?.summary || {};
  const filteredUsers = (report?.users || []).filter(u =>
    (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.user_id || '').includes(search)
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-spread-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)]">Spread Management</h2>
            <p className="text-xs text-[var(--text-secondary)] mt-1">Track and manage per-trade spread across all users</p>
          </div>
          <button onClick={loadReport} data-testid="spread-refresh-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-green-500/15 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/25 border border-green-500/20">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading && !report ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="spread-summary">
              <div className="p-4 rounded-lg border bg-amber-500/10 text-amber-400 border-amber-500/20" data-testid="spread-total">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Total Spread</span>
                </div>
                <p className="text-lg font-bold font-mono">{fmt(s.total_spread)}</p>
              </div>
              <div className="p-4 rounded-lg border bg-emerald-500/10 text-emerald-400 border-emerald-500/20" data-testid="spread-paid">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Paid</span>
                </div>
                <p className="text-lg font-bold font-mono">{fmt(s.total_paid)}</p>
              </div>
              <div className="p-4 rounded-lg border bg-red-500/10 text-red-400 border-red-500/20" data-testid="spread-unpaid">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Unpaid</span>
                </div>
                <p className="text-lg font-bold font-mono">{fmt(s.total_unpaid)}</p>
              </div>
              <div className="p-4 rounded-lg border bg-blue-500/10 text-blue-400 border-blue-500/20" data-testid="spread-user-count">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Users with Spread</span>
                </div>
                <p className="text-lg font-bold font-mono">{s.users_with_spread || 0}</p>
              </div>
            </div>

            {/* Per-User Table */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid="spread-users-table">
              <div className="px-5 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] whitespace-nowrap">Per-User Spread</h3>
                <div className="relative max-w-xs w-full">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
                  <input type="text" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
                    data-testid="spread-user-search"
                    className="w-full pl-9 pr-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500/50" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] text-[var(--text-tertiary)] uppercase border-b border-[var(--border-subtle)]">
                      <th className="text-left px-5 py-2.5">User</th>
                      <th className="text-right px-3 py-2.5">Total Spread</th>
                      <th className="text-right px-3 py-2.5">Paid</th>
                      <th className="text-right px-3 py-2.5">Unpaid</th>
                      <th className="text-right px-3 py-2.5">Trades</th>
                      <th className="text-center px-3 py-2.5">Status</th>
                      <th className="text-right px-5 py-2.5">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)] transition-colors" data-testid={`spread-row-${u.user_id}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-bold text-green-400">{u.name?.[0]?.toUpperCase() || '?'}</span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-[var(--text-primary)]">{u.name || 'Unknown'}</p>
                              <p className="text-[10px] text-[var(--text-tertiary)]">{u.email} <span className="font-mono">#{u.user_id}</span></p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-amber-400">{fmt(u.total_spread)}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-emerald-400">{fmt(u.paid_spread)}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-red-400">{fmt(u.unpaid_spread)}</td>
                        <td className="px-3 py-3 text-right text-xs text-[var(--text-secondary)]">
                          <span className="font-mono">{u.total_trades}</span>
                          <span className="text-[10px] text-[var(--text-tertiary)] ml-1">({u.paid_trades}p / {u.unpaid_trades}u)</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {u.unpaid_spread > 0 ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/15 text-red-400" data-testid={`spread-status-${u.user_id}`}>Unpaid</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-400" data-testid={`spread-status-${u.user_id}`}>Cleared</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex gap-1.5 justify-end">
                            {u.unpaid_spread > 0 && (
                              <button
                                onClick={() => handleMarkPaid(u.user_id, u.name)}
                                disabled={actionLoading === u.user_id + '_pay'}
                                data-testid={`spread-mark-paid-${u.user_id}`}
                                className="px-2.5 py-1 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors disabled:opacity-40"
                              >
                                {actionLoading === u.user_id + '_pay' ? '...' : 'Mark Paid'}
                              </button>
                            )}
                            {u.paid_spread > 0 && (
                              <button
                                onClick={() => handleReset(u.user_id, u.name)}
                                disabled={actionLoading === u.user_id + '_reset'}
                                data-testid={`spread-reset-${u.user_id}`}
                                className="px-2.5 py-1 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors disabled:opacity-40"
                              >
                                {actionLoading === u.user_id + '_reset' ? '...' : 'Reset'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-xs text-[var(--text-tertiary)]">No users with spread activity</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
